import { IOffer, IOrder, IConsumptionPointNode } from 'types/order';

export interface IConsumptionPoint {
    id: string;
    name: string;
}

export const CONSUMPTION_POINT_FILTER_ALL = 'ALL';

// Prefer the French translation, fall back to the first non-empty name.
function pickConsumptionPointName(translations: IConsumptionPointNode['translations'] | undefined): string {
    const list = translations ?? [];
    const fr = list.find((translation) => translation.language === 'fr')?.name;
    if (fr) return fr;
    return list.find((translation) => !!translation.name)?.name ?? '';
}

// The consumption point the guest chose, read from the order (V2). Null for legacy / no choice.
export function getConsumptionPoint(order: IOrder): IConsumptionPoint | null {
    const consumptionPoint = order?.consumptionPoint;
    if (!consumptionPoint) return null;
    return { id: consumptionPoint.id, name: pickConsumptionPointName(consumptionPoint.translations) };
}

// All consumption points connected to the selected offer templates (their union) — the filter options.
export function getAvailableConsumptionPoints(offers: IOffer[] | undefined): IConsumptionPoint[] {
    const byId = new Map<string, IConsumptionPoint>();
    (offers ?? []).forEach((offer) => {
        (offer.consumptionPoints ?? []).forEach((consumptionPoint) => {
            if (!byId.has(consumptionPoint.id)) {
                byId.set(consumptionPoint.id, {
                    id: consumptionPoint.id,
                    name: pickConsumptionPointName(consumptionPoint.translations),
                });
            }
        });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// Consumption points that actually appear on the given orders. Keeps points filterable even when
// they were removed/changed on the offer template after the orders were placed (V2 orders carry
// their own consumption point; legacy orders carry none).
export function getConsumptionPointsFromOrders(orders: IOrder[] | undefined): IConsumptionPoint[] {
    const byId = new Map<string, IConsumptionPoint>();
    (orders ?? []).forEach((order) => {
        const consumptionPoint = getConsumptionPoint(order);
        if (consumptionPoint && !byId.has(consumptionPoint.id)) {
            byId.set(consumptionPoint.id, consumptionPoint);
        }
    });
    return Array.from(byId.values());
}

// Merge consumption-point lists, deduping by id, sorted by name.
export function mergeConsumptionPoints(...lists: IConsumptionPoint[][]): IConsumptionPoint[] {
    const byId = new Map<string, IConsumptionPoint>();
    lists.forEach((list) => list.forEach((consumptionPoint) => {
        if (!byId.has(consumptionPoint.id)) byId.set(consumptionPoint.id, consumptionPoint);
    }));
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}
